import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PlayerService } from "../../shared/services/player.service";
import { AuthRedirectService } from "../../shared/services/auth-redirect.service";
import {
  RatingReviewPlayer,
  RatingReviewProgress,
  RatingReviewSession,
} from "../../shared/models/ratingReview";
import { BehaviorSubject } from "rxjs";
import { Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "rating-review",
    templateUrl: "./rating-review.component.html",
    styleUrls: ["../../player/player.component.css", "../event.component.css"],
    standalone: false
})
export class RatingReviewComponent implements OnChanges, OnInit, OnDestroy {
  @Input() reviewId: string;
  @Output() closed = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>();

  public review: RatingReviewSession;
  public progress: RatingReviewProgress;
  public currentIndex: number = 0;
  public offence: number;
  public defence: number;
  public loading: boolean = false;
  public readonly reviewReady$ = new BehaviorSubject<boolean>(false);
  public saving: boolean = false;
  public errorMsg: string;
  public completedReview: boolean = false;
  public routeMode: boolean = false;

  private routeSubscription: Subscription;

  constructor(
    private playerService: PlayerService,
    private authRedirect: AuthRedirectService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      const routeReviewId = params.get("reviewId");
      if (!routeReviewId) {
        return;
      }

      this.routeMode = true;
      if (routeReviewId !== this.reviewId) {
        this.reviewId = routeReviewId;
        this.loadReview();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reviewId && this.reviewId) {
      this.loadReview();
    }
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  public get currentPlayer(): RatingReviewPlayer {
    if (!this.review || !this.review.players || this.review.players.length === 0) {
      return null;
    }

    return this.review.players[this.currentIndex];
  }

  public get playerDisplayName(): string {
    const player = this.currentPlayer;
    if (!player) {
      return "";
    }

    return player.nickName || player.name;
  }

  public selectOffence(value: number): void {
    this.offence = value;
  }

  public selectDefence(value: number): void {
    this.defence = value;
  }

  public saveCurrentPlayer(): void {
    if (!this.currentPlayer || !this.offence || !this.defence || this.saving) {
      return;
    }

    this.saving = true;
    this.errorMsg = null;
    this.playerService.updateRatingReviewPlayer(this.reviewId, this.currentPlayer.id, {
      offence: this.offence,
      defence: this.defence,
    }).subscribe(
      (progress) => {
        this.markCurrentPlayerRated();
        this.handleProgress(progress);
      },
      (error) => this.handleSaveError(error)
    );
  }

  public skipCurrentPlayer(): void {
    if (!this.currentPlayer || this.saving) {
      return;
    }

    this.saving = true;
    this.errorMsg = null;
    this.playerService.skipRatingReviewPlayer(this.reviewId, this.currentPlayer.id).subscribe(
      (progress) => {
        this.currentPlayer.skipped = true;
        this.currentPlayer.rated = false;
        this.handleProgress(progress);
      },
      (error) => this.handleSaveError(error)
    );
  }

  public finishReview(): void {
    if (this.saving) {
      return;
    }

    this.saving = true;
    this.errorMsg = null;
    this.playerService.completeRatingReview(this.reviewId).subscribe(
      (progress) => this.showCompleted(progress),
      (error) => this.handleSaveError(error)
    );
  }

  public close(): void {
    this.closed.emit();
    if (this.routeMode) {
      this.router.navigate(["/notifications"]);
    }
  }

  public done(): void {
    this.completed.emit();
    if (this.routeMode) {
      this.router.navigate(["/notifications"]);
    }
  }

  public isFinalPlayer(): boolean {
    return this.review && this.review.players && this.findNextPendingIndex() === -1;
  }

  public getPlayerInitials(player: RatingReviewPlayer): string {
    const displayName = this.getPlayerDisplayName(player).trim();
    if (!displayName || displayName === "-") {
      return "?";
    }

    const parts = displayName.split(/[\s._-]+/).filter(Boolean);
    if (parts.length > 1) {
      return parts.slice(0, 2).map((name) => name.charAt(0).toUpperCase()).join("");
    }

    return displayName.slice(0, 2).toUpperCase();
  }

  public playerAvatarTone(player: RatingReviewPlayer): string {
    const displayName = this.getPlayerDisplayName(player).trim();
    const hash = displayName.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return "avatar-initial-tone-" + (hash % 6);
  }

  public getPlayerDisplayName(player: RatingReviewPlayer): string {
    return player ? (player.nickName || player.name || "-") : "-";
  }

  public showPlayerFullName(player: RatingReviewPlayer): boolean {
    return !!(player && player.name && player.nickName && player.name !== player.nickName);
  }

  public setOffenceFromSlider(rating: number): void {
    this.offence = this.normalizedSliderRating(rating);
  }

  public setDefenceFromSlider(rating: number): void {
    this.defence = this.normalizedSliderRating(rating);
  }

  public ratingSliderPercent(rating: number): number {
    if (!rating || rating <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, rating * 10));
  }

  public displaySliderRating(rating: number): number {
    const roundedRating = this.normalizedSliderRating(rating);
    return roundedRating > 0 ? roundedRating : null;
  }

  public profileStatValue(value: number): number | string {
    if (!this.currentPlayer || !this.currentPlayer.profileStatsSummary) {
      return "–";
    }
    return value != null ? value : 0;
  }

  public teamGoalScore(): string {
    if (!this.currentPlayer || !this.currentPlayer.profileStatsSummary) {
      return "–";
    }
    const value = this.currentPlayer.profileStatsSummary.teamGoalDifference != null
      ? this.currentPlayer.profileStatsSummary.teamGoalDifference
      : 0;
    return value > 0 ? "+" + value : String(value);
  }

  public joinedAt(): Date {
    if (this.currentPlayer && this.currentPlayer.profileStatsSummary && this.currentPlayer.profileStatsSummary.joinedAt) {
      return new Date(this.currentPlayer.profileStatsSummary.joinedAt);
    }

    return null;
  }

  private loadReview(): void {
    this.loading = true;
    this.reviewReady$.next(false);
    this.errorMsg = null;
    this.completedReview = false;
    this.review = null;
    this.progress = null;

    this.playerService.getRatingReview(this.reviewId).subscribe(
      (review) => {
        this.review = review;
        if (review.status === "COMPLETED") {
          this.showCompleted({
            id: review.id,
            status: review.status,
            playersTotal: review.playersTotal,
            playersRated: this.countPlayers("rated"),
            playersSkipped: this.countPlayers("skipped"),
            playersRemaining: review.playersRemaining,
          });
          return;
        }

        this.currentIndex = this.findFirstPendingIndex();
        this.applyCurrentRating();
        this.loading = false;
        this.reviewReady$.next(true);
      },
      (error) => {
        this.errorMsg = this.getErrorMessage(error, this.translate.instant('ratingReview.errors.loadFailed'));
        this.handleAuthError(error);
        this.loading = false;
        this.reviewReady$.next(true);
      }
    );
  }

  private handleProgress(progress: RatingReviewProgress): void {
    this.progress = progress;
    this.saving = false;

    if (progress.status === "COMPLETED") {
      this.showCompleted(progress);
      return;
    }

    const nextIndex = this.findNextPendingIndex();
    if (nextIndex === -1) {
      this.finishReview();
      return;
    }

    this.currentIndex = nextIndex;
    this.applyCurrentRating();
  }

  private showCompleted(progress: RatingReviewProgress): void {
    this.progress = progress;
    this.completedReview = true;
    this.loading = false;
    this.saving = false;
    this.reviewReady$.next(true);
  }

  private markCurrentPlayerRated(): void {
    this.currentPlayer.rated = true;
    this.currentPlayer.skipped = false;
    this.currentPlayer.currentRating = {
      offence: this.offence,
      defence: this.defence,
    };
  }

  private applyCurrentRating(): void {
    const player = this.currentPlayer;
    if (player && player.currentRating) {
      this.offence = player.currentRating.offence;
      this.defence = player.currentRating.defence;
      return;
    }

    this.offence = null;
    this.defence = null;
  }

  private normalizedSliderRating(rating: number): number {
    return Math.max(0, Math.min(10, Math.round(Number(rating) || 0)));
  }

  private findFirstPendingIndex(): number {
    if (!this.review || !this.review.players) {
      return 0;
    }

    const index = this.review.players.findIndex((player) => !player.rated && !player.skipped);
    return index === -1 ? 0 : index;
  }

  private findNextPendingIndex(): number {
    if (!this.review || !this.review.players) {
      return -1;
    }

    for (let i = this.currentIndex + 1; i < this.review.players.length; i++) {
      if (!this.review.players[i].rated && !this.review.players[i].skipped) {
        return i;
      }
    }

    for (let i = 0; i < this.currentIndex; i++) {
      if (!this.review.players[i].rated && !this.review.players[i].skipped) {
        return i;
      }
    }

    return -1;
  }

  private countPlayers(status: "rated" | "skipped"): number {
    if (!this.review || !this.review.players) {
      return 0;
    }

    return this.review.players.filter((player) => player[status]).length;
  }

  private handleSaveError(error: any): void {
    this.errorMsg = this.getErrorMessage(error, this.translate.instant('ratingReview.errors.saveFailed'));
    this.handleAuthError(error);
    this.saving = false;
  }

  private handleAuthError(error: any): void {
    if (error && (error.status === 401 || error.status === 403)) {
      this.authRedirect.login().subscribe();
    }
  }

  private getErrorMessage(response: any, fallback: string): string {
    if (response && response.error) {
      if (response.error.message) {
        return response.error.message;
      }
      if (response.error.errorMsg) {
        return response.error.errorMsg;
      }
      if (typeof response.error === "string") {
        return response.error;
      }
    }

    return fallback;
  }
}
